<script lang="ts" setup>
import Select from 'primevue/select'
import FloatLabel from 'primevue/floatlabel'
import Message from 'primevue/message'
import { Form, FormField } from '@primevue/forms'
import InputText from "primevue/inputtext"
import Button from "primevue/button"
import Fieldset from "primevue/fieldset"
import { AVAILABLE_DEXCHANGES, AVAILABLE_GMGN_CHAINS } from '@/utils/consts'
import { computed, ref, useTemplateRef } from 'vue'
import { zodResolver } from '@primevue/forms/resolvers/zod'
import { i18n } from '@/i18n'
import { z } from 'zod'
import type { FormInstance, FormSubmitEvent } from '@primevue/forms'
import AdaptiveDialog from '../AdaptiveDialog.vue'
import { useEyeDexStore } from '@/stores/eyeDexStore'
import { useMyToast } from '@/composable/useMyToast'
import { useAssetsStore } from '@/stores/assetsStore'
import { capitalize, isUndefined } from 'lodash'
import type { AssetExchangeContractMinResponse } from '@/services/assets-v2-service/types'
import { useRouter } from 'vue-router'

const props = defineProps<{
  loading: boolean
  assets?: string[]
}>()

const form = useTemplateRef<FormInstance>('form')
const formLoading = ref(false)
const dexStore = useEyeDexStore()
const assetsStore = useAssetsStore()
const { t } = i18n.global
const visible = ref(false)
const toggle = () => {
  visible.value = !visible.value
}

const initValues = {
  asset: '',
  cex: 'mexc',
  chain: '',
  contract: ''
}


const { addToast } = useMyToast()

const onFormSubmit = async (e: FormSubmitEvent) => {
  if (!e.valid) {
    return
  }
  formLoading.value = true
  try {
    const actionId = `${e.values["cex"]}/${e.values["asset"]}/${e.values["chain"]}/${e.values["contract"]}`
    await dexStore.putTokenAddress(actionId, e.values["cex"], e.values["asset"], e.values["chain"], e.values["contract"], 'swap')
    if (dexStore.errors[actionId]) {
      addToast({
        severity: "error",
        message: t(`modal.addToken.errors.${dexStore.errors[actionId].message.detail[0]?.type}`)
      })
    } else {

      addToast({
        severity: "success",
        message: t(`modal.addToken.success`)
      })
      await assetsStore.fetchMyContracts()
    }
  } finally {
    formLoading.value = false
  }
}

const resolver = zodResolver(
  z.object({
    asset: z.string().min(1, { message: t('assetEmptyError') }),
    cex: z.string().min(1, { message: t('cexEmptyError') }),
    chain: z.string().min(1, { message: t('chainEmptyError') }),
    contract: z.string().min(1, { message: t('contractEmptyError') }),
  }),
)

const finLoading = computed(() => {
  return props.loading || formLoading.value
})

const router = useRouter()
const getBunchUrl = (k: AssetExchangeContractMinResponse) => {
  const routeData = router.resolve({
    name: 'eye-dex-asset-long',
    params: {
      marketType: 'swap',
      asset: k.asset_name,
      cex: k.exchange_name,
      chain: k.chain_name
    },
  })
  return routeData.href
}

const deleteContract = async (k: AssetExchangeContractMinResponse) => {
  const kk = assetsStore.myContracts?.indexOf(k)
  if (!isUndefined(kk) && kk > -1) {
    assetsStore.myContracts?.splice(kk, 1)
  }
  await assetsStore.deleteContract(k.id)
}

defineExpose({ toggle })
</script>

<template>
  <AdaptiveDialog v-model:visible="visible" pt:root="w-200" pt:content="p-2" pt:header="px-4 py-1"
    :header="$t('modal.addToken.title')">

    <div class="px-2 mb-4">

      <Form ref="form" id="add-token-form" class="grid p-2 grid-cols-1 lg:grid-cols-3 gap-y-7 gap-x-2"
        :initial-values="initValues" :resolver @submit="onFormSubmit">
        <FormField v-slot="$field" name="cex">
          <FloatLabel variant="on">
            <Select fluid :options="AVAILABLE_DEXCHANGES" option-label="title" option-value="value"
              :disabled="finLoading" :loading="finLoading"></Select>
            <label>{{ $t('cex') }}</label>
          </FloatLabel>
          <Message v-if="$field?.invalid" severity="error" size="small" variant="simple">{{ $t($field.error?.message) }}
          </Message>
        </FormField>
        <!-- Торговая пара -->
        <FormField v-slot="$field" name="asset" class="relative">
          <FloatLabel variant="on">
            <InputText :disabled="finLoading" fluid autocomplete="off" placeholder="" />
            <label>{{ $t('symbol') }}</label>
          </FloatLabel>
          <Message class="absolute" v-if="$field?.invalid" severity="error" size="small" variant="simple">{{
            $t($field.error?.message) }}
          </Message>
        </FormField>
        <!-- Сеть -->
        <FormField v-slot="$field" name="chain" class="relative">
          <FloatLabel variant="on">
            <Select :disabled="finLoading" :loading="finLoading" :options="AVAILABLE_GMGN_CHAINS" fluid>
              <template #empty>{{ $t('emptyMessage') }}</template>
            </Select>
            <label>{{ $t('chain') }}</label>
          </FloatLabel>
          <Message class="absolute" v-if="$field?.invalid" severity="error" size="small" variant="simple">{{
            $t($field.error?.message) }}
          </Message>
        </FormField>
        <!-- Сеть -->
        <div class="col-span-3 grid grid-cols-5">
          <FormField v-slot="$field" name="contract" class="relative col-span-4">
            <FloatLabel variant="on">
              <InputText :disabled="finLoading" :loading="finLoading" fluid class="rounded-r-none" autocomplete="off"
                placeholder="">
              </InputText>
              <label>{{ $t('contractAddress') }}</label>
            </FloatLabel>
            <Message class="absolute" v-if="$field?.invalid" severity="error" size="small" variant="simple">{{
              $t($field.error?.message) }}
            </Message>
          </FormField>
          <Button :loading="finLoading" form="add-token-form" class="rounded-l-none" :disabled="loading" type="submit"
            severity="secondary">{{
              $t('next')
            }}</Button>
        </div>
      </Form>

      <Fieldset :legend="$t('addedContracts')" v-if="(assetsStore?.myContracts?.length ?? 0) > 0">
        <div class="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-2 gap-4 px-2 mt-2">
          <div v-for="k, i in assetsStore.myContracts" :key="i" class="flex w-full">
            <Button as="a" class="rounded-r-none w-full" :href="getBunchUrl(k)" target="_blank" size="small"
              severity="secondary">{{
                capitalize(k.exchange_name)
              }}: {{
                k.chain_name.toUpperCase()
              }}, {{
                k.asset_name.toUpperCase()
              }}</Button>
            <Button @click="deleteContract(k)" size="small" severity="secondary" class="rounded-l-none"
              icon="pi pi-times"></Button>
          </div>
        </div>
      </Fieldset>


    </div>
  </AdaptiveDialog>
</template>
